import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {Box, Button, Checkbox, Dialog, DialogContent, FormControlLabel, IconButton, Radio, RadioGroup, TextField, Tooltip, Typography} from '@mui/material';
import DashboardShell from '../components/DashboardShell';
import CpcLoader from '../components/CpcLoader';
import {DashboardProvider, useDashboard} from '../context/DashboardContext';
import {toastSuccess, toastWarning} from '../../../utils/utils';
import {
    getCpcGovIndicatorDetailService,
    getCpcGovUserInfoService,
    isCpcGovLoggedIn,
    rejectEvidenceFile,
    uploadCpcGovEvidenceService,
} from '../../../services/cpcGov.service';
import adaptIndicatorDetail, {adaptProposalToForm} from '../adaptIndicatorDetail';
import {
    ACCEPTED_EVIDENCE,
    EVIDENCE_FIELDS,
    IMPL_STATUS,
    LEGAL_STATUS,
    PROPOSER_FIELDS,
    REASON_GROUPS,
    STATUS_MEANING,
    TRACE_REQS,
    VALIDATION_NOTICE_LONG,
    VALIDATION_OUTCOMES,
    confidenceColors,
    isValidEmail,
    isValidNumeric,
    isValidUrl,
    isValidYear,
    itemNotice,
    rationaleHeading,
    revisionComplete,
    revisionMissingFields,
    scoreColor,
    sourceLines,
    stakeById,
    statusColors,
    statusLabel,
    targetById,
} from '../data';

const C = {
    // navy/navyDeep/white stay literal — see the matching comment in
    // NationalOverview.page.jsx. Plain text/background roles use the
    // dedicated keys below, which flip via --cpc-* (see darkMode.css).
    navy: '#003C69', navyDeep: '#003D69', cyan: '#0096C8', cyanDeep: '#007DB4',
    white: '#FFFFFF', mist: 'var(--cpc-surface-subtle, #F5F8FB)', muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))', rust: '#c0553a', green: '#5c9469',
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const cardSx = {background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))'};

const noticeSx = (kind) => (kind === 'warn'
    ? {background: '#fdf6f2', borderColor: 'rgba(192,85,58,.28)', color: '#a3452d'}
    : {background: '#f2f6f8', borderColor: 'rgba(63,122,148,.28)', color: '#2f5f75'});

const decisionDotColor = (decision) => (decision === 'agree' ? C.green : '#c99a3f');
const decisionFgColor = (decision) => (decision === 'agree' ? C.green : '#8a6a1a');

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        background: C.mist,
        fontSize: 14,
        fontFamily: 'inherit',
        color: C.navyDeep,
        '& fieldset': {borderColor: 'rgba(0,60,105,.2)'},
    },
};

const labelSx = {fontSize: 13, fontWeight: 700, color: C.text};
const reqSx = {color: C.rust};
const fieldHelpSx = {fontSize: 13, color: C.muted, mb: 1};
const h3Sx = {fontSize: 15, fontWeight: 700, color: C.text, margin: '22px 0 8px'};
const radioOptionSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    padding: '5px 10px',
    border: '1px solid var(--cpc-border, rgba(0,60,105,.14))',
    marginBottom: 2,
    fontSize: 13.5,
    color: C.text,
    ml: 0,
};

const LabeledField = ({label, help, caption, error, req, area, value, onChange, placeholder, readOnly, sx}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75, alignSelf: 'start', ...sx}}>
        <Typography component="span" sx={labelSx}>{label} {req && <Box component="span" sx={reqSx}>*</Box>}</Typography>
        {help && <Typography sx={{...fieldHelpSx, margin: 0}}>{help}</Typography>}
        <TextField
            fullWidth
            size="small"
            variant="outlined"
            multiline={!!area}
            minRows={area ? 3 : undefined}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            error={!!error}
            InputProps={readOnly ? {readOnly: true} : undefined}
            sx={fieldSx}
        />
        {error ? (
            <Typography sx={{fontSize: 12, color: C.rust, fontWeight: 600, margin: 0}}>{error}</Typography>
        ) : caption ? (
            <Typography sx={{fontSize: 12, color: C.muted, margin: 0}}>{caption}</Typography>
        ) : null}
    </Box>
);
const FieldRow = ({fields, sx}) => {
    const stackedAreas = fields.map((f) => `"${f.key}-label" "${f.key}-help" "${f.key}-input"`).join(' ');
    const rowAreas = [
        fields.map((f) => `${f.key}-label`).join(' '),
        fields.map((f) => `${f.key}-help`).join(' '),
        fields.map((f) => `${f.key}-input`).join(' '),
    ].map((row) => `"${row}"`).join(' ');

    return (
        <Box
            sx={{
                display: 'grid',
                rowGap: 0.75,
                columnGap: 1.75,
                gridTemplateColumns: {xs: '1fr', sm: `repeat(${fields.length}, 1fr)`},
                gridTemplateAreas: {xs: stackedAreas, sm: rowAreas},
                ...sx,
            }}
        >
            {fields.map((f) => (
                <React.Fragment key={f.key}>
                    <Typography component="span" sx={{...labelSx, gridArea: `${f.key}-label`}}>
                        {f.label} {f.req && <Box component="span" sx={reqSx}>*</Box>}
                    </Typography>
                    <Typography sx={{...fieldHelpSx, margin: 0, gridArea: `${f.key}-help`}}>{f.help || ''}</Typography>
                    <Box sx={{gridArea: `${f.key}-input`, display: 'flex', flexDirection: 'column', gap: 0.5}}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder={f.placeholder}
                            value={f.value}
                            onChange={f.onChange}
                            error={!!f.error}
                            InputProps={f.readOnly ? {readOnly: true} : undefined}
                            sx={fieldSx}
                        />
                        {f.error ? (
                            <Typography sx={{fontSize: 12, color: C.rust, fontWeight: 600, margin: 0}}>{f.error}</Typography>
                        ) : f.caption ? (
                            <Typography sx={{fontSize: 12, color: C.muted, margin: 0}}>{f.caption}</Typography>
                        ) : null}
                    </Box>
                </React.Fragment>
            ))}
        </Box>
    );
};

/** Splits an array into chunks of at most `size` items, in order. */
const chunk = (arr, size) => arr.reduce((rows, item, i) => {
    if (i % size === 0) rows.push([]);
    rows[rows.length - 1].push(item);
    return rows;
}, []);

/**
 * Unsaved Propose Revision input, kept in sessionStorage so it survives
 * closing the dialog without saving (the X, "Cancel", clicking outside) and
 * even a session logout — logging out only clears the auth tokens
 * (clearCpcGovSession), never sessionStorage — so re-opening the same item
 * later in this tab restores exactly what was typed. Cleared once the
 * revision is actually saved, since the saved copy is authoritative then.
 */
const REVISION_DRAFTS_KEY = 'cpcGovRevisionDrafts';

const readRevisionDrafts = () => {
    try {
        return JSON.parse(sessionStorage.getItem(REVISION_DRAFTS_KEY) || '{}');
    } catch (_) {
        return {};
    }
};

const saveRevisionDraft = (key, form) => {
    try {
        const all = readRevisionDrafts();
        all[key] = form;
        sessionStorage.setItem(REVISION_DRAFTS_KEY, JSON.stringify(all));
    } catch (_) {
        /* ignore — the draft just won't survive this time */
    }
};

const clearRevisionDraft = (key) => {
    try {
        const all = readRevisionDrafts();
        delete all[key];
        sessionStorage.setItem(REVISION_DRAFTS_KEY, JSON.stringify(all));
    } catch (_) {
        /* ignore */
    }
};

const baseForm = (item, uinfo) => ({
    noScoreChange: false,
    proposedRubric: null,
    propValue: '', propUnit: '', propYear: '', propCoverage: '',
    reasons: [], otherReason: '',
    evTitle: '', evIssuer: '', evDate: '', evJurisdiction: '', evLocation: '', evExcerpt: '', evUrl: '',
    legalStatus: '', implStatus: '',
    pName: uinfo.fullName || '', pOrg: uinfo.org || '', pDept: '', pRole: uinfo.job || '', pEmail: uinfo.email || '',
    focalPoint: '', supervising: '', techContact: '',
    itemKind: item.src === 'ExternalFixed' ? 'value' : 'rubric',
});

const IndicatorDetailPage = () => {
    const history = useHistory();
    const {code} = useParams();
    const {itemDecision, setItemDecision} = useDashboard();
    // Collapsed by default — Overview and Assessment Items share one page
    // now, so every item card starting open would make it very long to
    // scroll through. Expanding is an opt-in way to look at one item at a
    // time.
    const [expandedItems, setExpandedItems] = useState({});
    const toggleItemCollapse = (code) => setExpandedItems((e) => ({...e, [code]: !e[code]}));
    const [openItemCode, setOpenItemCode] = useState(null);
    const [form, setForm] = useState(null);
    const [errorText, setErrorText] = useState('');
    // True only after a Save attempt has actually failed — required fields
    // don't turn red just for being empty while the reviewer is still filling
    // the form in for the first time.
    const [showErrors, setShowErrors] = useState(false);

    /**
     * Evidence attachments for the item whose form is currently open (API-06).
     *
     * Keyed to `openItemCode` and cleared whenever a different item's form is
     * opened, so one item's attachments can never be shown under another's.
     */
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);

    /**
     * Live data (API-03), scoped to the signed-in reviewer's own country.
     *
     * The URL carries the EXTERNAL id ("Indicator 017") because that is what the
     * Explorer shows; the API accepts either that or the canonical
     * "CPCI Indicator 017" and resolves it, so nothing here has to know the
     * mapping.
     */
    const [apiInd, setApiInd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    /**
     * Gate on the User Information page's "Are you authorized to submit
     * evidence and propose revisions on behalf of your organization?"
     * answer — only reviewers who answered Yes may Agree, propose a
     * revision, or edit a proposal already in progress.
     *
     * Defaults to not-allowed until resolved (rather than optimistically
     * allowing while the check is in flight) since the buttons gated by
     * this are ones that persist a review decision.
     */
    const [authInfo, setAuthInfo] = useState({loaded: false, allowed: false});

    useEffect(() => {
        let alive = true;

        const fromSession = () => {
            try {
                const saved = JSON.parse(sessionStorage.getItem('cpcGovernmentUserInfo') || '{}');
                return saved.authorized === 'Yes';
            } catch (_) {
                return false;
            }
        };

        if (!isCpcGovLoggedIn()) {
            setAuthInfo({loaded: true, allowed: fromSession()});
            return undefined;
        }

        getCpcGovUserInfoService()
            .then((res) => {
                if (!alive) return;
                setAuthInfo({loaded: true, allowed: res?.data?.profile?.authorized_to_submit === true});
            })
            .catch(() => {
                if (alive) setAuthInfo({loaded: true, allowed: fromSession()});
            });

        return () => { alive = false; };
    }, []);

    const canSubmit = authInfo.allowed;
    const authTooltip = !authInfo.loaded
        ? 'Checking your authorization…'
        : 'Only reviewers authorized to submit evidence and proposed revisions can do this — update this on the User Information page.';

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setLoadError('');

        getCpcGovIndicatorDetailService(code)
            .then((res) => {
                if (!alive) return;
                setApiInd(adaptIndicatorDetail(res?.data));
            })
            .catch((err) => {
                if (!alive) return;
                setApiInd(null);
                setLoadError(err?.message || 'Could not load this indicator.');
            })
            .finally(() => { if (alive) setLoading(false); });

        return () => { alive = false; };
    }, [code]);

    /**
     * API only — there is deliberately NO fallback to the static INDICATORS list.
     *
     * The mockup's sample codes (FC-05, RC-10, TI-14 ...) are the same STYLE the
     * external names may take: external_code_map exists so Dr. Park can rename
     * external ids without a frontend redeploy, and his mockup already uses that
     * form. So a fallback keyed on `code` could start matching real codes at any
     * time and would then answer a FAILED load with mock content that looks real.
     *
     * A country reviewer acting on invented assessment data is far worse than a
     * screen that says it could not load. Show the error.
     */
    const ind = apiInd;

    useEffect(() => {
        document.title = ind ? ind.name : 'Indicator Detail';
    }, [ind]);

    /**
     * Child indicators have no detail screen — send them back to the Explorer.
     *
     * Seong Min (Q3): "In this case, the user cannot access SCR-DTL at all. This
     * is because clicking is disabled in SCR-EXP." Disabling the row covers the
     * click, but a typed, bookmarked or shared URL does not go through the row,
     * and without this the page renders a 0-item checklist WITH live Agree and
     * Propose Revision controls — inviting a review decision on an indicator that
     * is scored from the Children's Assessment and cannot be reviewed here.
     *
     * `replace` rather than `push` so Back does not bounce straight back in.
     */
    useEffect(() => {
        if (ind && ind.isChild) {
            history.replace('/dq_pro/cpc-government-dashboard/explorer');
        }
    }, [ind, history]);

    if (loading && !ind) {
        return (
            <DashboardShell active="explorer">
                <Box sx={{padding: '40px 56px 90px', '@media (max-width: 720px)': {padding: '24px 18px 64px'}}}>
                    <CpcLoader label="Loading indicator…"/>
                </Box>
            </DashboardShell>
        );
    }

    if (!ind) {
        return (
            <DashboardShell active="explorer">
                <Box sx={{padding: '40px 56px 90px', '@media (max-width: 1439px)': {paddingLeft: '40px', paddingRight: '28px'}, '@media (max-width: 720px)': {padding: '24px 18px 64px'}}}>
                    <Tooltip title="Return to the Checklist Explorer" arrow>
                        <Button
                            onClick={() => history.goBack()}
                            sx={{border: 'none', background: 'transparent', color: C.cyanDeep, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, textTransform: 'none', padding: 0, minWidth: 'auto', mb: 1.75, '&:hover': {background: 'transparent'}}}
                        >
                            ← Back to all indicators
                        </Button>
                    </Tooltip>
                    <Typography>
                        {loadError || 'Indicator not found.'}
                    </Typography>
                </Box>
            </DashboardShell>
        );
    }

    const stake = stakeById(ind.s);
    const target = targetById(ind.t);
    const sc = statusColors(ind.status);
    const itemsDecided = ind.items.filter((it) => itemDecision(ind.code, it.code)?.decision).length;
    // The item the Propose Revision dialog is currently open for, or null.
    // Looked up once here rather than inside the items.map() below, since the
    // dialog itself now renders once after that loop, not once per item.
    const openItem = ind.items.find((it) => it.code === openItemCode) || null;
    // Only populated after a failed Save — see setShowErrors in submitRevision.
    const missingFields = showErrors ? revisionMissingFields(form) : [];
    const fieldErrorSx = {border: `1px solid ${C.rust}`};

    let uinfo = {};
    try {
        uinfo = JSON.parse(sessionStorage.getItem('cpcGovernmentUserInfo') || '{}');
    } catch (_) { /* ignore */ }

    const openRevision = (item) => {
        const existing = itemDecision(ind.code, item.code);
        const f = baseForm(item, uinfo);
        /**
         * The saved proposal has to be TRANSLATED, not spread.
         *
         * A server proposal is snake_case with `evidence` and `proposer` nested;
         * the form is flat camelCase. Only `reasons` and `supervising` share a
         * name, so spreading it straight on left 24 of 26 fields blank and the
         * reviewer saw an empty form where their saved work should have been.
         */
        const saved = adaptProposalToForm(existing?.revision);
        // An actually-saved proposal wins over any leftover draft — a draft is
        // only what's left from typing that was never saved, and the saved
        // copy is what's authoritative once it exists.
        const draft = !saved ? readRevisionDrafts()[`${ind.code}::${item.code}`] : null;
        setForm(saved ? {...f, ...saved} : draft ? {...f, ...draft} : f);
        setOpenItemCode(item.code);
        setErrorText('');
        // A fresh form (or a saved one being reopened) starts with no red
        // borders — those only appear after this SPECIFIC attempt to save
        // fails, not just because a required field happens to be empty.
        setShowErrors(false);

        /**
         * Show the documents already attached to THIS item.
         *
         * Re-opening a saved proposal has to show its attachments, or the
         * reviewer concludes the upload was lost and uploads the same file
         * again. They come from the server with the rest of the proposal
         * (`revision.evidence.files`), so read them from there rather than
         * keeping a separate client-side list.
         *
         * Reset per item — attachments belong to one item and must never carry
         * over to the next form that is opened.
         */
        setUploadedFiles(existing?.revision?.evidence?.files || []);
        setUploadError('');
        setUploading(false);
    };

    // Shared by the dialog's X, its "Cancel" button, and clicking outside it —
    // whatever was typed is kept as a draft (see readRevisionDrafts above)
    // rather than thrown away, since closing the dialog isn't the same as
    // deciding to discard the input.
    const closeRevisionDialog = () => {
        if (openItemCode && form) {
            saveRevisionDraft(`${ind.code}::${openItemCode}`, form);
        }
        setOpenItemCode(null);
        setForm(null);
        setErrorText('');
    };

    // Drafts save on every change, not just on close — a session logout
    // reloads the page immediately (invokeApi's 401 handler), which happens
    // too abruptly for closeRevisionDialog's save-on-close to run in time.
    const updateForm = (patch) => setForm((f) => {
        const next = {...f, ...patch};
        if (openItemCode) saveRevisionDraft(`${ind.code}::${openItemCode}`, next);
        return next;
    });

    /**
     * Send one evidence file for the item whose form is open (API-06).
     *
     * The client-side check is a courtesy so an oversized or wrong-format file
     * is refused instantly instead of after a long upload; the server repeats
     * every rule and is the actual enforcement.
     */
    const handleEvidenceUpload = async (file) => {
        const why = rejectEvidenceFile(file);
        if (why) {
            setUploadError(why);
            return;
        }

        setUploadError('');
        setUploading(true);
        try {
            const res = await uploadCpcGovEvidenceService(ind.code, openItemCode, file);
            const saved = res?.data?.file;
            if (saved) {
                setUploadedFiles((prev) => [...prev, saved]);
            } else {
                setUploadError('The file did not upload. Please try again.');
            }
        } catch (e) {
            // Report the server's own wording — it explains WHY it was refused.
            setUploadError(e?.message || 'The file did not upload. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const toggleReason = (id) => setForm((f) => {
        const next = {
            ...f,
            reasons: f.reasons.includes(id) ? f.reasons.filter((x) => x !== id) : [...f.reasons, id],
        };
        if (openItemCode) saveRevisionDraft(`${ind.code}::${openItemCode}`, next);
        return next;
    });

    const submitRevision = (item) => {
        if (!revisionComplete(form)) {
            setErrorText('Complete all required (*) fields before saving.');
            setShowErrors(true);
            return;
        }
        /**
         * Confirm the save, and say so if it FAILED.
         *
         * This used to close the form and clear it without waiting for the
         * result, so a rejected save looked identical to a successful one — the
         * form vanished either way and the reviewer's evidence was silently
         * gone. Agree already reported both outcomes; a revision carries far
         * more work, so it matters more here.
         *
         * The form is only torn down once the save resolves. On failure it
         * stays open with the reviewer's input intact so they can retry.
         */
        const isEdit = !!itemDecision(ind.code, item.code)?.revision;

        setItemDecision(ind.code, item.code, 'propose', form)
            .then(() => {
                toastSuccess(isEdit ? 'Revision updated successfully' : 'Revision proposed successfully');
                // Now saved for real — any leftover draft from before this save
                // would only ever be stale from here on.
                clearRevisionDraft(`${ind.code}::${item.code}`);
                setOpenItemCode(null);
                setForm(null);
            })
            .catch((err) => {
                toastWarning(err?.message || 'Could not save your revision. Please try again.');
                setErrorText('Your revision was not saved. Please try again.');
            });
    };

    const agree = (item) => {
        setItemDecision(ind.code, item.code, 'agree', null)
            .then(() => toastSuccess('Assessment confirmed successfully'))
            // setItemDecision already rolled the local state back — this is
            // just telling the reviewer the click did not actually save.
            .catch((err) => toastWarning(err?.message || 'Could not save your decision. Please try again.'));
    };

    return (
        <DashboardShell active="explorer">
            <Box sx={{padding: '40px 56px 90px', '@media (max-width: 1439px)': {paddingLeft: '40px', paddingRight: '28px'}, '@media (max-width: 720px)': {padding: '24px 18px 64px'}}}>
                <Tooltip title="Return to the Checklist Explorer" arrow>
                    <Button
                        onClick={() => history.goBack()}
                        sx={{border: 'none', background: 'transparent', color: C.cyanDeep, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, textTransform: 'none', padding: 0, minWidth: 'auto', mb: 1.75, '&:hover': {background: 'transparent'}}}
                    >
                        ← Back to all indicators
                    </Button>
                </Tooltip>
                <Typography sx={{fontSize: 13, color: C.muted, mb: 3.25}}>
                    Checklist Explorer › {stake?.name} › {target?.name} › {ind.name}
                </Typography>

                <Box sx={{...cardSx, mb: 4.5}}>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, padding: '18px 24px 0'}}>
                        <Box component="span" sx={{fontSize: 12.5, fontWeight: 700, padding: '6px 11px', background: C.mist, color: C.text}}>Current status: {statusLabel(ind.status)}</Box>
                        <Box component="span" sx={{fontSize: 12.5, fontWeight: 700, padding: '6px 11px', background: C.mist, color: C.text}}>{ind.items.length} Assessment Items</Box>
                        <Box component="span" sx={{fontSize: 12.5, fontWeight: 700, padding: '6px 11px', background: confidenceColors(ind.conf).bg, color: confidenceColors(ind.conf).fg}}>Evidence Confidence: {ind.conf}</Box>
                    </Box>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1.7fr 1fr'}}}>
                        <Box sx={{padding: '18px 24px 26px'}}>
                            <Typography sx={{fontSize: 22, fontWeight: 700, color: C.text, margin: '4px 0 12px'}}>{ind.name}</Typography>
                            <Typography sx={{fontSize: 15, lineHeight: 1.55, color: C.muted, margin: 0}}>{ind.question}</Typography>
                        </Box>
                        <Box
                            sx={{
                                padding: '18px 24px 26px',
                                background: C.mist,
                                borderLeft: {md: '1px solid var(--cpc-border, rgba(0,60,105,.1))'},
                                borderTop: {xs: '1px solid var(--cpc-border, rgba(0,60,105,.1))', md: 'none'},
                            }}
                        >
                            <Typography sx={{fontSize: 12.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.5}}>
                                Indicator Status
                            </Typography>
                            <Box component="span" sx={{fontSize: 12.5, fontWeight: 700, padding: '6px 11px', display: 'inline-block', mb: 1.25, background: sc.bg, color: sc.fg}}>
                                {statusLabel(ind.status)}
                            </Box>
                            <Typography sx={{fontSize: 13.5, color: C.muted, lineHeight: 1.5}}>{STATUS_MEANING[ind.status]}</Typography>
                        </Box>
                    </Box>
                </Box>

                <Typography component="h2" sx={{fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 6px'}}>
                    {ind.items.length} Assessment Items and Supporting Evidence
                </Typography>
                <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.muted, mb: 1.75}}>{itemsDecided} of {ind.items.length} decided</Typography>
                <Typography sx={{fontSize: 14.5, lineHeight: 1.55, color: C.muted, margin: '0 0 8px', maxWidth: 820}}>
                    Each assessment item presents the current score, assessment rationale, supporting evidence,
                    source information, and confidence level.
                </Typography>
                <Typography sx={{fontSize: 14.5, lineHeight: 1.55, color: C.muted, margin: '0 0 8px', maxWidth: 820}}>
                    Select <Box component="strong" sx={{color: C.green}}>Agree</Box> to confirm the assessment, or{' '}
                    <Box component="strong" sx={{color: C.rust}}>Propose Revision</Box> to submit a different score supported
                    by verifiable evidence.
                </Typography>

                {/*
                  * The 15 Child indicators carry no item-level detail by design —
                  * they are scored from the Children's Assessment, not from
                  * checklist items. Saying so is important: an empty list here
                  * otherwise reads as a page that failed to load.
                  */}
                {ind.items.length === 0 && (
                    <Box sx={{mt: 2.5, padding: '18px 20px', background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))'}}>
                        <Typography sx={{fontSize: 14.5, lineHeight: 1.55, color: C.text}}>
                            This indicator has no assessment items to review. Its score comes from the
                            Children&rsquo;s Assessment rather than from the Government Checklist, so there is
                            nothing to agree with or propose a revision to on this screen.
                        </Typography>
                    </Box>
                )}

                <Box sx={{mt: 2.5}}>
                    {ind.items.map((it, idx) => {
                        const notice = itemNotice(it);
                        /**
                         * A saved DRAFT is not a decision.
                         *
                         * review/state returns an entry for an item that has EITHER a
                         * decision or a revision proposal, and synthesises
                         * `{decision: null, revision: {...}}` when only the proposal
                         * exists — which happens when a save is interrupted partway
                         * (Seong Min, cp246-1: the proposal row was written, the
                         * decision row never was).
                         *
                         * Testing that entry for truthiness read the draft as decided,
                         * and the item became a dead end: the footer showed "Revision
                         * proposed" but offered no Edit proposal button (that needs
                         * decision === 'propose'), and no Agree / Propose Revision
                         * buttons either (those need no entry at all). Nothing on the
                         * card could be clicked, and because `itemsDecided` counts
                         * `?.decision`, the indicator sat on "18 of 19 decided" forever.
                         *
                         * Keyed on the decision VALUE so a draft-only entry falls back
                         * to the undecided branch. The draft is not lost: openRevision()
                         * reads the raw entry and restores the saved form, and isEdit
                         * keys on `?.revision`, so submitting updates that proposal
                         * instead of creating a second one.
                         */
                        const entry = itemDecision(ind.code, it.code);
                        const decision = entry?.decision ? entry : null;
                        const scColor = scoreColor(typeof it.raw === 'number' && it.outOf === 100 ? it.raw : (it.raw / it.outOf) * 100);
                        /**
                         * Drop source lines with no value.
                         *
                         * sourceLines() picks a line set from the item's type, but with
                         * live data the value behind one can legitimately be absent —
                         * an item with no country evidence AND no borrowed mean has no
                         * estimation basis to name. Rendering the label with nothing
                         * after it reads as a field that failed to load.
                         */
                        const lines = sourceLines(it).filter((sl) => String(sl.v || '').trim());

                        const isCollapsed = !expandedItems[it.code];

                        return (
                            <Box key={it.code} sx={{...cardSx, padding: '22px 24px', mb: 2.5}}>
                                <Box
                                    onClick={() => toggleItemCollapse(it.code)}
                                    sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: isCollapsed ? 0 : 1.75, cursor: 'pointer'}}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5}}>
                                        <Tooltip title={isCollapsed ? 'Click to expand this item' : 'Click to collapse this item'} arrow>
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    flex: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mt: 'auto',
                                                    mr: '13px',
                                                    ml: '-8px',
                                                    border: `1.5px dashed ${C.cyanDeep}`,
                                                    borderRadius: '10px',
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: 20,
                                                        lineHeight: 1,
                                                        fontWeight: 700,
                                                        color: C.cyanDeep,
                                                        transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                                                        transition: 'transform .15s ease',
                                                    }}
                                                >
                                                    ▾
                                                </Box>
                                            </Box>
                                        </Tooltip>
                                        <Box>
                                            <Typography sx={{fontSize: 12, fontWeight: 700, color: C.muted, mb: 0.5}}>
                                                {ind.code} · Item {idx + 1} of {ind.items.length} · {it.code}
                                            </Typography>
                                            <Typography component="span" sx={{fontSize: 16.5, fontWeight: 700, color: C.text}}>{it.name}</Typography>
                                            <Box component="span" sx={{fontSize: 11.5, fontWeight: 700, padding: '4px 9px', ml: 1.25, background: confidenceColors(it.conf).bg, color: confidenceColors(it.conf).fg}}>
                                                Confidence: {it.conf}
                                            </Box>
                                            {isCollapsed && (
                                                <Box
                                                    component="span"
                                                    sx={{fontSize: 12, fontWeight: 700, ml: 1.25, display: 'inline-flex', alignItems: 'center', gap: 0.5, color: decision ? decisionFgColor(decision.decision) : C.muted}}
                                                >
                                                    <Box sx={{width: 7, height: 7, borderRadius: '50%', flex: 'none', background: decision ? decisionDotColor(decision.decision) : 'var(--cpc-border, rgba(0,60,105,.3))'}}/>
                                                    {decision ? (decision.decision === 'agree' ? 'Agreed — confirmed' : 'Revision proposed') : 'Not reviewed'}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{textAlign: 'right'}}>
                                        <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted}}>Current Score</Typography>
                                        <Typography
                                            sx={{
                                                // Both no-score states are phrases, not numbers, so
                                                // neither can carry the 26px numeral size.
                                                fontSize: typeof it.raw === 'number' ? 26 : 15,
                                                fontWeight: 700,
                                                color: scColor,
                                            }}
                                        >
                                            {/*
                                              * Three distinct states, per Seong Min's SCR-DTL deck:
                                              *
                                              *   a number              -> "2 / 5" (or bare for /100)
                                              *   estimated             -> "No data—regional estimate"
                                              *   no value, no estimate -> "No data available"
                                              *
                                              * Both no-score states are spelled out rather than shown
                                              * as a dash, because they mean different things and the
                                              * reviewer has to be able to tell them apart at a glance:
                                              * one has a regional figure standing in for the country,
                                              * the other has nothing at all.
                                              *
                                              * Seong Min confirmed the split (5:02 PM): "If you are
                                              * referring to the item-level score, we need to display
                                              * 'no data available'. This is because in such cases
                                              * final_score_source stores a specific level's REGION,
                                              * rather than necessarily being AI or EXT."
                                              */}
                                            {typeof it.raw === 'number'
                                                ? <>{it.raw}{it.outOf === 100 ? '' : ` / ${it.outOf}`}</>
                                                : (it.estimate_level ? 'No data—regional estimate' : 'No data available')}
                                        </Typography>
                                    </Box>
                                </Box>

                                {!isCollapsed && (
                                <>

                                {notice && (
                                    <Box sx={{border: '1px solid', padding: '14px 16px', mb: 1.75, ...noticeSx(notice.kind)}}>
                                        <Typography sx={{fontSize: 13.5, fontWeight: 700, mb: 0.5}}>{notice.title}</Typography>
                                        {notice.body.map((b) => <Typography key={b} sx={{fontSize: 13, lineHeight: 1.5, margin: '4px 0 0'}}>{b}</Typography>)}
                                    </Box>
                                )}

                                {it.measures && (
                                    <>
                                        <Typography sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.cyanDeep, mb: 0.75}}>
                                            This item measures
                                        </Typography>
                                        <Typography sx={{fontSize: 14, color: C.text, mb: 1.75}}>{it.measures}</Typography>
                                    </>
                                )}

                                {it.q && (
                                    <>
                                        <Typography sx={{fontSize: 14.5, fontWeight: 600, color: C.text, mb: 1.25}}>{it.q}</Typography>
                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.75}}>
                                            {it.opts.map((opt, i) => {
                                                const isCurrent = it.cur === i + 1;
                                                return (
                                                    <Box
                                                        key={opt}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            fontSize: 13.5,
                                                            color: C.text,
                                                            padding: '7px 10px',
                                                            background: isCurrent ? 'var(--cpc-highlight, #E0EFF6)' : C.mist,
                                                            borderLeft: `3px solid ${isCurrent ? C.cyanDeep : 'transparent'}`,
                                                        }}
                                                    >
                                                        <strong>{i + 1}.</strong> {opt}
                                                        {isCurrent && <Typography component="span" sx={{fontSize: 10.5, fontWeight: 700, color: C.cyanDeep, ml: 'auto'}}>CURRENT</Typography>}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </>
                                )}

                                {it.rationale && (
                                    <Box sx={{background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.08))', padding: '14px 16px', mb: 1.75}}>
                                        <Typography sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.cyanDeep, mb: 0.75}}>
                                            {rationaleHeading(it)}
                                        </Typography>
                                        <Typography sx={{fontSize: 14, lineHeight: 1.55, color: C.text, margin: 0}}>{it.rationale}</Typography>
                                    </Box>
                                )}

                                {/*
                                  * PBDQ-175 — AI-scored items show their citation claims.
                                  * Seong Min: "Since the Source is AI, the value of
                                  * 'arbiter_citation_claims' should be displayed."
                                  * The adapter sets this only when score_source is AI.
                                  */}
                                {it.citationClaims && (
                                    <Box sx={{background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.08))', padding: '14px 16px', mb: 1.75}}>
                                        <Typography sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.cyanDeep, mb: 0.75}}>
                                            Citation claims
                                        </Typography>
                                        <Typography sx={{fontSize: 14, lineHeight: 1.55, color: C.text, margin: 0}}>{it.citationClaims}</Typography>
                                    </Box>
                                )}

                                {/*
                                  * PBDQ-175 — Supporting Evidence is LINKS ONLY.
                                  *
                                  * Seong Min, 2:46 PM: "Supporting Evidence simply needs to
                                  * display links. Item organization information must not be
                                  * displayed along with them", and: "Since Citation claims have
                                  * been separated, content like 'DQI-Desktop' should be removed
                                  * from Supporting Evidence."
                                  *
                                  * `sourceDetails` is `item_data_source — item_organization`,
                                  * which on AI-scored items reads "DQI-Desktop — DQ Institute" —
                                  * DQ's own research tool, not evidence a government can verify.
                                  * The claims behind the score now have their own Citation Claims
                                  * block, so this card carries the citable links and nothing else.
                                  *
                                  * Gated on the URLS now, not on sourceDetails: an item with a
                                  * source name but no links would otherwise render an empty card.
                                  */}
                                {it.urls?.length > 0 && it.src !== 'ExternalFixed' && (
                                    <Box sx={{background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.08))',padding: '14px 16px', mb: 2.5}}>
                                       <Typography sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.cyanDeep, mb: 0.75}}>
                                            Supporting Evidence
                                        </Typography>
                                        {it.urls.map((u) => (
                                            <Box component="a" key={u} href={u} target="_blank" rel="noreferrer" sx={{fontSize: 13, color: C.cyanDeep, wordBreak: 'break-all', display: 'block'}}>{u}</Box>
                                        ))}
                                    </Box>
                                )}

                                {lines.length > 0 && (
                                    <Box sx={{mt: 1.25, mb: 1.75}}>
                                        {lines.map((sl) => (
                                            <Typography key={sl.k} sx={{fontSize: 13, color: C.muted, mb: 0.25}}>
                                                <Box component="span" sx={{fontWeight: 700, color: C.text}}>{sl.k}</Box> {sl.v}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {!decision ? (
                                    <Box sx={{display: 'flex', gap: 2.25, mt: 1.5}}>
                                        <Tooltip title={canSubmit ? 'Confirm the current assessment is accurate' : authTooltip} arrow>
                                            <span>
                                                <Button disabled={!canSubmit} onClick={() => agree(it)} sx={{border: '2px solid', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, padding: '10px 16px', background: C.surface, textTransform: 'none', borderColor: C.green, color: C.green, '&:hover': {background: C.surface}}}>
                                                    ✓ Agree
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={canSubmit ? 'Submit a different score backed by evidence' : authTooltip} arrow>
                                            <span>
                                                <Button disabled={!canSubmit} onClick={() => openRevision(it)} sx={{border: '2px solid', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, padding: '10px 16px', background: C.surface, textTransform: 'none', borderColor: C.rust, color: C.rust, '&:hover': {background: C.surface}}}>
                                                    Propose Revision
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                ) : (
                                    <Box sx={{display: 'flex', gap: 2.25, mt: 1.5, alignItems: 'center'}}>
                                        <Box sx={{fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, color: decisionFgColor(decision.decision)}}>
                                            <Box sx={{width: 8, height: 8, borderRadius: '50%', background: decisionDotColor(decision.decision)}}/>
                                            {decision.decision === 'agree' ? 'Agreed — confirmed' : 'Revision proposed'}
                                        </Box>
                                        {decision.decision === 'propose' && (
                                            <Tooltip title={canSubmit ? 'Reopen and edit your submitted proposal' : authTooltip} arrow>
                                                <span>
                                                    <Button disabled={!canSubmit} onClick={() => openRevision(it)} sx={{border: '2px solid', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, padding: '10px 16px', background: C.surface, textTransform: 'none', borderColor: C.rust, color: C.rust, '&:hover': {background: C.surface}}}>
                                                        Edit proposal
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                                </>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                                <Dialog
                                    open={!!openItem}
                                    onClose={closeRevisionDialog}
                                    maxWidth="md"
                                    fullWidth
                                    scroll="paper"
                                    // Rendered through a portal onto document.body, outside
                                    // DashboardShell's .cpc-gov-scope wrapper — reapplied here so
                                    // the dark-mode Typography override (scoped to exclude only
                                    // .cpc-gov-scope's DOM descendants) doesn't clobber this
                                    // dialog's own text colors.
                                    PaperProps={{className: 'cpc-gov-scope', sx: {maxHeight: '95vh'}}}
                                >
                                    {openItem && form && (
                                        <DialogContent sx={{padding: '28px 32px 32px', background: C.mist, overflowY: 'auto'}}>
                                            <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: -2}}>
                                                <Tooltip title="Close without saving — your entries are kept as a draft" arrow>
                                                    <IconButton
                                                        onClick={closeRevisionDialog}
                                                        size="small"
                                                        aria-label="Close"
                                                        sx={{color: C.muted}}
                                                    >
                                                        ✕
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        <Typography component="h2" sx={{fontSize: 19, fontWeight: 700, color: C.text, margin: '0 0 8px'}}>Propose a Revision</Typography>
                                        <Typography sx={{fontSize: 14, color: C.muted, lineHeight: 1.5, margin: '0 0 4px'}}>
                                            Use this form when the current score, underlying data value, rationale,
                                            or supporting source does not accurately reflect the latest authoritative
                                            and verifiable national evidence.
                                        </Typography>
                                        <Typography sx={{fontSize: 14, color: C.muted, lineHeight: 1.5, margin: '0 0 4px'}}>
                                            Complete all required fields so the CPC Index review team can assess the
                                            proposal without requesting additional clarification.
                                        </Typography>

                                        <Typography component="h3" sx={h3Sx}>1. Proposed Result</Typography>
                                        <FormControlLabel
                                            sx={{ml: 0, mb: 0.75, alignItems: 'flex-start'}}
                                            control={
                                                <Checkbox
                                                    checked={form.noScoreChange}
                                                    onChange={(e) => updateForm({noScoreChange: e.target.checked})}
                                                    size="small"
                                                    sx={{color: C.cyanDeep, '&.Mui-checked': {color: C.cyanDeep}, pt: 0}}
                                                />
                                            }
                                            label={<Typography sx={{fontSize: 13.5, color: C.text}}><strong>No score change requested</strong> — source or rationale correction only.</Typography>}
                                        />

                                        {!form.noScoreChange && form.itemKind === 'rubric' && (
                                            <>
                                                <Typography sx={{...labelSx, display: 'block'}}>Proposed score <Box component="span" sx={reqSx}>*</Box></Typography>
                                                <Typography sx={fieldHelpSx}>Select one of the rubric levels available for this item.</Typography>
                                                <Box sx={missingFields.includes('proposedRubric') ? {...fieldErrorSx, padding: '10px'} : undefined}>
                                                    <RadioGroup
                                                        name={`rubric-${openItem.code}`}
                                                        value={form.proposedRubric != null ? String(form.proposedRubric) : ''}
                                                        onChange={(e) => updateForm({proposedRubric: Number(e.target.value)})}
                                                    >
                                                        {openItem.opts.map((opt, i) => (
                                                            <FormControlLabel
                                                                key={opt}
                                                                value={String(i + 1)}
                                                                control={<Radio size="small"/>}
                                                                label={<><strong>{i + 1}.</strong> {opt}</>}
                                                                sx={radioOptionSx}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                </Box>
                                            </>
                                        )}

                                        {!form.noScoreChange && form.itemKind === 'value' && (
                                            <>
                                                <Typography sx={{...labelSx, display: 'block'}}>Proposed official value <Box component="span" sx={reqSx}>*</Box></Typography>
                                                <Typography sx={fieldHelpSx}>
                                                    Enter the revised official country value, unit of measurement, and reference year.
                                                </Typography>
                                                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 1.75}}>
                                                    <LabeledField
                                                        label="Proposed value" req value={form.propValue}
                                                        onChange={(e) => updateForm({propValue: e.target.value})}
                                                        placeholder="e.g. 84.2"
                                                        error={!isValidNumeric(form.propValue) ? 'Enter a number.' : missingFields.includes('propValue') ? 'Required.' : ''}
                                                    />
                                                    <LabeledField
                                                        label="Unit" req value={form.propUnit}
                                                        onChange={(e) => updateForm({propUnit: e.target.value})}
                                                        placeholder="e.g. % of schools"
                                                        error={missingFields.includes('propUnit') ? 'Required.' : ''}
                                                    />
                                                    <LabeledField
                                                        label="Reference year" req value={form.propYear}
                                                        onChange={(e) => updateForm({propYear: e.target.value})}
                                                        placeholder="e.g. 2025"
                                                        error={!isValidYear(form.propYear) ? 'Enter a 4-digit year.' : missingFields.includes('propYear') ? 'Required.' : ''}
                                                    />
                                                    <LabeledField label="Reporting population or coverage" value={form.propCoverage} onChange={(e) => updateForm({propCoverage: e.target.value})} placeholder="Required where applicable"/>
                                                </Box>
                                                <Typography sx={{...fieldHelpSx, mt: 1.25}}>
                                                    The CPC Index system will calculate the corresponding score using
                                                    the established scoring methodology. The submitting official
                                                    should not manually calculate or enter the normalized CPC Index score.
                                                </Typography>
                                            </>
                                        )}

                                        <Typography component="h3" sx={h3Sx}>2. Primary Reason for Revision</Typography>
                                        <Typography sx={fieldHelpSx}>
                                            Select all reasons that apply to this proposal.
                                        </Typography>
                                        <Box sx={missingFields.includes('reasons') ? {...fieldErrorSx, padding: '10px'} : undefined}>
                                            {REASON_GROUPS.map((g) => (
                                                <Box key={g.title}>
                                                    <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.text, margin: '14px 0 8px'}}>{g.title}</Typography>
                                                    {g.options.map((o) => (
                                                        <Box key={o.id} sx={{display: 'flex', alignItems: 'flex-start', gap: 1.25, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '10px 12px', mb: 1}}>
                                                            <FormControlLabel
                                                                control={<Checkbox size="small" checked={form.reasons.includes(o.id)} onChange={() => toggleReason(o.id)} sx={{pt: 0}}/>}
                                                                sx={{ml: 0, alignItems: 'flex-start', flex: 1}}
                                                                label={
                                                                    <Box>
                                                                        <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.text}}>{o.label}</Typography>
                                                                        <Typography sx={{fontSize: 12.5, color: C.muted, mt: 0.25}}>{o.desc}</Typography>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ))}
                                        </Box>
                                        {form.reasons.includes('other') && (
                                            <Box sx={{mt: 1}}>
                                                <LabeledField
                                                    label="Explain the other material reason"
                                                    req
                                                    area
                                                    value={form.otherReason}
                                                    onChange={(e) => updateForm({otherReason: e.target.value})}
                                                    placeholder="Explain clearly why the proposed revision does not fall within the categories above."
                                                    error={missingFields.includes('otherReason') ? 'Required.' : ''}
                                                />
                                            </Box>
                                        )}

                                        <Typography component="h3" sx={h3Sx}>3. Supporting Evidence</Typography>
                                        <Typography component="span" sx={{...labelSx, display: 'block', mb: 0.75}}>Evidence files</Typography>
                                        <Typography sx={fieldHelpSx}>Upload the supporting documents or data files.</Typography>
                                        {/*
                                          * The real upload control (API-06).
                                          *
                                          * This dashed panel came from the prototype as inert markup — it
                                          * LOOKED like a drop zone and did nothing, so a reviewer clicking
                                          * it saw no response and reasonably concluded upload was broken.
                                          * It is now a real <label>: clicking anywhere in it opens the file
                                          * picker, and dragging a file onto it uploads too.
                                          *
                                          * Ruled by Seong Min, 5 Aug 11:29: documents only — docx and pdf —
                                          * at up to 50 MB. Anything else (spreadsheets, presentations,
                                          * datasets) is supplied as a URL in "Source URL" below, and the
                                          * AI agent retrieves it.
                                          */}
                                        <Box
                                            component="label"
                                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                            onDragLeave={() => setDragOver(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDragOver(false);
                                                const f = e.dataTransfer.files && e.dataTransfer.files[0];
                                                if (f) handleEvidenceUpload(f);
                                            }}
                                            sx={{
                                                display: 'block',
                                                border: `2px dashed ${dragOver ? C.cyanDeep : 'var(--cpc-border, rgba(0,60,105,.25))'}`,
                                                padding: '22px',
                                                textAlign: 'center',
                                                background: dragOver ? 'var(--cpc-border, rgba(0,60,105,.04))' : C.mist,
                                                mb: 1.5,
                                                cursor: uploading ? 'default' : 'pointer',
                                                '&:hover': {borderColor: uploading ? 'var(--cpc-border, rgba(0,60,105,.25))' : C.cyanDeep},
                                            }}
                                        >
                                            <input
                                                type="file"
                                                hidden
                                                accept=".pdf,.docx"
                                                disabled={uploading}
                                                onChange={(e) => {
                                                    const f = e.target.files && e.target.files[0];
                                                    // Reset so re-picking the SAME file fires onChange again.
                                                    e.target.value = '';
                                                    if (f) handleEvidenceUpload(f);
                                                }}
                                            />
                                            {uploading
                                                ? 'Uploading…'
                                                : <>📎 Drag &amp; drop files, or <strong>browse files</strong></>}
                                            <Typography sx={{fontSize: 12, color: C.muted, mt: 0.5}}>PDF, DOCX &middot; max 50 MB per file</Typography>
                                            <Typography sx={{fontSize: 12, color: C.muted, mt: 0.25}}>
                                                For spreadsheets, datasets or other formats, add a link under &ldquo;Source URL&rdquo; instead.
                                            </Typography>
                                        </Box>

                                        {uploadError ? (
                                            <Typography sx={{fontSize: 12.5, color: C.rust, mb: 1.5, fontWeight: 600}}>
                                                {uploadError}
                                            </Typography>
                                        ) : null}

                                        {uploadedFiles.length ? (
                                            <Box sx={{mb: 1.5}}>
                                                {uploadedFiles.map((uf, n) => (
                                                    <Typography
                                                        key={`${uf.file_name}-${n}`}
                                                        sx={{fontSize: 12.5, color: C.text, display: 'flex', gap: 0.75}}
                                                    >
                                                        <Box component="span" sx={{color: C.green, fontWeight: 700}}>✓</Box>
                                                        {uf.file_name}
                                                        <Box component="span" sx={{color: C.muted}}>
                                                            ({(uf.size_bytes / (1024 * 1024)).toFixed(1)} MB)
                                                        </Box>
                                                    </Typography>
                                                ))}
                                            </Box>
                                        ) : null}
                                        <Typography component="span" sx={{...labelSx, display: 'block', mb: 1}}>Accepted evidence may include:</Typography>
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 4}}>
                                            {ACCEPTED_EVIDENCE.map((e) => (
                                                <Box key={e} component="span" sx={{fontSize: 12, fontWeight: 600, padding: '5px 10px', background: C.mist, color: C.text, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))'}}>{e}</Box>
                                            ))}
                                        </Box>

                                        {chunk(EVIDENCE_FIELDS.filter((ef) => !ef.area), 3).map((row, i) => (
                                            <FieldRow
                                                key={row.map((ef) => ef.k).join('-')}
                                                sx={{mb: i < 1 ? 3 : 1.75}}
                                                fields={row.map((ef) => ({
                                                    key: ef.k,
                                                    label: ef.label,
                                                    help: ef.help,
                                                    req: ef.req,
                                                    value: form[ef.k],
                                                    onChange: (e) => updateForm({[ef.k]: e.target.value}),
                                                    placeholder: ef.ph,
                                                    error: ef.k === 'evUrl' && !isValidUrl(form.evUrl)
                                                        ? 'Enter a full https:// link.'
                                                        : missingFields.includes(ef.k) ? 'Required.' : '',
                                                }))}
                                            />
                                        ))}
                                        {EVIDENCE_FIELDS.filter((ef) => ef.area).map((ef) => (
                                            <LabeledField
                                                key={ef.k}
                                                label={ef.label}
                                                help={ef.help}
                                                req={ef.req}
                                                area
                                                value={form[ef.k]}
                                                onChange={(e) => updateForm({[ef.k]: e.target.value})}
                                                placeholder={ef.ph}
                                            />
                                        ))}


                                        {/*
                                          * Fixed warm-tinted card, same as noticeSx's warn/info
                                          * boxes above — background AND text both stay literal
                                          * (never var(--cpc-*, ...)) since this is a self-contained
                                          * highlight card, not a plain page surface. Pairing a
                                          * flip-with-theme text color against a background that
                                          * intentionally never flips is exactly how text goes
                                          * invisible — light-mode text on a light-mode-only bg.
                                          */}
                                        <Box sx={{border: '1px solid rgba(192,85,58,.28)', background: '#faf6f2', padding: '16px 18px', mt: 3}}>
                                            <Typography sx={{fontSize: 14, fontWeight: 700, color: C.rust, mb: 0.75}}>Evidence Traceability Requirements</Typography>
                                            <Typography sx={{fontSize: 12.5, color: C.navyDeep, mb: 0.5, lineHeight: 1.5}}>Evidence must be specific, authoritative, and independently traceable.</Typography>
                                            {TRACE_REQS.map((t) => (
                                                <Typography key={t.h} sx={{fontSize: 12.5, color: C.navyDeep, mb: 0.5, lineHeight: 1.5}}><strong>{t.h}</strong> {t.b}</Typography>
                                            ))}
                                            <Typography sx={{fontSize: 12.5, color: C.navyDeep, mb: 0.5, lineHeight: 1.5}}>
                                                A general link to a ministry homepage, an undated webpage, a
                                                search-results page, or an unsupported statement should not be
                                                considered sufficient evidence.
                                            </Typography>
                                        </Box>

                                        <Typography component="h3" sx={h3Sx}>4. Legal or Policy Status</Typography>
                                        <Typography sx={fieldHelpSx}>
                                            Select one option when the evidence concerns a law, regulation, policy,
                                            strategy, standard, or formal government instrument.
                                        </Typography>
                                        <RadioGroup name="legalStatus" value={form.legalStatus} onChange={(e) => updateForm({legalStatus: e.target.value})}>
                                            {LEGAL_STATUS.map((opt) => (
                                                <FormControlLabel key={opt} value={opt} control={<Radio size="small"/>} label={opt} sx={radioOptionSx}/>
                                            ))}
                                        </RadioGroup>

                                        <Typography component="h3" sx={h3Sx}>5. Implementation Status</Typography>
                                        <Typography sx={fieldHelpSx}>
                                            Select one option when the item concerns implementation, enforcement,
                                            service delivery, programme operation, or institutional capability.
                                        </Typography>
                                        <RadioGroup name="implStatus" value={form.implStatus} onChange={(e) => updateForm({implStatus: e.target.value})}>
                                            {IMPL_STATUS.map((opt) => (
                                                <FormControlLabel key={opt} value={opt} control={<Radio size="small"/>} label={opt} sx={radioOptionSx}/>
                                            ))}
                                        </RadioGroup>

                                        <Typography component="h3" sx={h3Sx}>6. Proposed By</Typography>
                                        <Typography sx={fieldHelpSx}>
                                            Provide the details of the person submitting the revision. Prefilled
                                            from your User Information — edit if the submitting official differs.
                                        </Typography>
                                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 1.75}}>
                                            {PROPOSER_FIELDS.map((pf) => (
                                                <LabeledField
                                                    key={pf.k}
                                                    label={pf.label}
                                                    req={pf.req}
                                                    value={form[pf.k]}
                                                    onChange={(e) => updateForm({[pf.k]: e.target.value})}
                                                    placeholder={pf.ph}
                                                    error={pf.k === 'pEmail' && !isValidEmail(form.pEmail)
                                                        ? 'Enter a valid email address.'
                                                        : missingFields.includes(pf.k) ? 'Required.' : ''}
                                                />
                                            ))}
                                            <LabeledField label="Submission date" caption="Automatically recorded" value={new Date().toLocaleDateString()} readOnly onChange={() => {}}/>
                                        </Box>

                                        <Typography component="span" sx={{...labelSx, display: 'block', margin: '0px 0 10px'}}>Optional</Typography>
                                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 1.75}}>
                                            <LabeledField label="Government focal-point confirmation" value={form.focalPoint} onChange={(e) => updateForm({focalPoint: e.target.value})} placeholder="Name and confirmation reference"/>
                                            <LabeledField label="Supervising authority" value={form.supervising} onChange={(e) => updateForm({supervising: e.target.value})} placeholder="Supervising body"/>
                                            <LabeledField label="Additional technical contact" value={form.techContact} onChange={(e) => updateForm({techContact: e.target.value})} placeholder="Name and email"/>
                                        </Box>

                                        <Box sx={{background: C.navyDeep, color: C.white, padding: '20px 22px', mt: 4.5}}>
                                            <Typography sx={{fontSize: 15, fontWeight: 700, mb: 1}}>Validation Notice</Typography>
                                            <Typography sx={{fontSize: 13.5, lineHeight: 1.55, color: 'rgba(255,255,255,.85)', mb: 1.75}}>{VALIDATION_NOTICE_LONG}</Typography>
                                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                                {VALIDATION_OUTCOMES.map((o) => (
                                                    <Box key={o.label} component="span" sx={{display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, fontWeight: 700, padding: '6px 11px', background: 'rgba(255,255,255,.08)'}}>
                                                        <Box sx={{width: 8, height: 8, borderRadius: '50%', background: o.color}}/>
                                                        {o.label}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                        <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', mt: 2.25, flexWrap: 'wrap'}}>
                                            <Tooltip title={canSubmit ? 'Submit this proposal for CPC Index validation' : authTooltip} arrow>
                                                <span>
                                                    <Button
                                                        disabled={!canSubmit}
                                                        onClick={() => submitRevision(openItem)}
                                                        sx={{border: `2px solid ${C.navy}`, background: C.navy, color: C.white, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, padding: '12px 22px', textTransform: 'none', '&:hover': {background: C.navyDeep}}}
                                                    >
                                                        Save proposed revision
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Close without saving — your entries are kept as a draft" arrow>
                                                <Button
                                                    onClick={closeRevisionDialog}
                                                    sx={{border: '2px solid var(--cpc-border, rgba(0,60,105,.25))', background: C.surface, color: C.text, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, padding: '12px 22px', textTransform: 'none', '&:hover': {background: C.surface}}}
                                                >
                                                    Cancel
                                                </Button>
                                            </Tooltip>
                                            {errorText && <Typography sx={{fontSize: 12.5, color: C.rust}}>{errorText}</Typography>}
                                        </Box>
                                        </DialogContent>
                                    )}
                                </Dialog>
            </Box>
        </DashboardShell>
    );
};

const IndicatorDetailPageWithProvider = () => (
    <DashboardProvider>
        <IndicatorDetailPage/>
    </DashboardProvider>
);

export default IndicatorDetailPageWithProvider;
